import { useCallback, useEffect, useMemo, useState } from "react";
import { SystemHealthDocument } from "#app/lib/data";
import { useAdminFetch } from "./admin-shared";

export type HealthStatus = "up" | "degraded" | "down";

export type ServiceHealthCheck = {
  name: string;
  kind: string;
  url: string;
  status: HealthStatus;
  latencyMs: number | null;
  lastCheck: string;
};

// Latency above this (ms) but still reachable counts as degraded.
const DEGRADED_MS = 600;

// Presentation metadata per backend service id. The probe data (reachability,
// latency, status code) comes from the server-side `systemHealth` query; only
// the human-facing name/kind live here.
const SERVICE_META: Record<string, { name: string; kind: string }> = {
  core: { name: "Core API", kind: "GraphQL · TypeORM" },
  auth: { name: "Auth", kind: "Login · tokens" },
  "feature-flags": { name: "Feature flags", kind: "GraphQL · TypeORM" },
  analytics: { name: "Analytics", kind: "GraphQL · TypeORM" },
};

/**
 * Live system health: the core API probes each service's `/health` endpoint
 * server-side and returns the aggregated status. Probing there reads real HTTP
 * status codes rather than opaque cross-origin reachability. No persisted
 * history (no uptime metric).
 */
export function useAdminHealth() {
  const { coreGql } = useAdminFetch();
  const [services, setServices] = useState<ServiceHealthCheck[]>([]);
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  const recheck = useCallback(async () => {
    setChecking(true);
    try {
      const data = await coreGql(SystemHealthDocument, {});
      const checkedAt = new Date(data.systemHealth.checkedAt).toLocaleTimeString();
      const results: ServiceHealthCheck[] = data.systemHealth.services.map((s) => {
        const meta = SERVICE_META[s.service] ?? { name: s.service, kind: "" };
        const status: HealthStatus = !s.healthy
          ? "down"
          : s.latencyMs != null && s.latencyMs > DEGRADED_MS
            ? "degraded"
            : "up";
        return {
          name: meta.name,
          kind: meta.kind,
          url: s.url,
          status,
          latencyMs: s.latencyMs ?? null,
          lastCheck: checkedAt,
        };
      });
      setServices(results);
      setLastChecked(checkedAt);
    } finally {
      setChecking(false);
    }
  }, [coreGql]);

  useEffect(() => {
    recheck();
  }, [recheck]);

  // Single pass over the (rarely-changing) service list for all the summary
  // counts, instead of five array walks on every consumer render.
  const summary = useMemo(() => {
    let upCount = 0;
    let degradedCount = 0;
    let downCount = 0;
    let latencyTotal = 0;
    let latencyCount = 0;
    for (const s of services) {
      if (s.status === "up") upCount += 1;
      else if (s.status === "degraded") degradedCount += 1;
      else downCount += 1;
      if (s.latencyMs != null) {
        latencyTotal += s.latencyMs;
        latencyCount += 1;
      }
    }
    return {
      upCount,
      degradedCount,
      downCount,
      avgMs: latencyCount ? Math.round(latencyTotal / latencyCount) : null,
    };
  }, [services]);

  return {
    services,
    checking,
    lastChecked,
    recheck,
    total: services.length,
    ...summary,
  };
}
